import React, { useContext, useEffect, useState, useCallback } from 'react'
import {
  Paper, Button, Snackbar, CircularProgress, IconButton, Tooltip, useTheme
} from '@mui/material'
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import HistoryIcon from '@mui/icons-material/History'
import hipersendService from '../../services/hipersend'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import Text from '../Text'
import ConfirmationModal from '../ConfirmationModal'
import Editor from './Editor'
import Historico from './Historico'
import { descreverRecorrencia } from '../../libs/scheduledMessages'

const sm = hipersendService.scheduledMessages

export const FUNCIONALIDADE = 'ScheduledMessages'

const STATUS = {
  active: { rotulo: 'Ativo', cor: '#25d366' },
  paused: { rotulo: 'Pausado', cor: '#ffb300' },
  finished: { rotulo: 'Concluído', cor: '#9aa0a6' }
}

function Selo({ texto, cor }) {
  return (
    <span
      className="text-xs font-bold uppercase px-2 py-1 rounded-full"
      style={{ color: cor, border: `1px solid ${cor}55`, backgroundColor: `${cor}1f` }}
    >
      {texto}
    </span>
  )
}

// "hoje, 09:00" / "sex, 14:30" / "12/09, 09:00" — data curta e sem depender de lib.
function formatarProxima(iso) {
  if (!iso) return null
  const data = new Date(iso)
  if (Number.isNaN(data.getTime())) return null

  const hora = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo'
  }).format(data)

  const diaDe = (d) => new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo'
  }).format(d)

  const hoje = new Date()
  const amanha = new Date(hoje.getTime() + 24 * 60 * 60 * 1000)

  if (diaDe(data) === diaDe(hoje)) return `hoje, ${hora}`
  if (diaDe(data) === diaDe(amanha)) return `amanhã, ${hora}`
  return `${diaDe(data)}, ${hora}`
}

export default function ScheduledMessages() {
  const theme = useTheme()
  const { getCredentials, user } = useContext(ApiDataWrapperContext)
  const { api, secret } = getCredentials()

  const [itens, setItens] = useState([])
  const [loading, setLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [mensagem, setMensagem] = useState('')
  const [emEdicao, setEmEdicao] = useState(null)
  const [verHistoricoDe, setVerHistoricoDe] = useState(null)
  const [isOpenConfirmationModalExcluir, setIsOpenConfirmationModalExcluir] = useState(false)
  const [paraExcluir, setParaExcluir] = useState(null)

  const notify = (msg) => setMensagem(msg)

  const carregar = useCallback(async () => {
    if (!api || !secret) return
    setLoading(true)
    try {
      setItens(await sm.list(api, secret))
    } catch (e) {
      notify('Falha ao carregar as mensagens agendadas')
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [api, secret])

  useEffect(() => { carregar() }, [carregar])

  const salvar = async (payload) => {
    setIsSaving(true)
    try {
      if (payload._id) await sm.update(api, secret, payload._id, payload)
      else await sm.create(api, secret, payload)
      setEmEdicao(null)
      await carregar()
      notify(payload._id ? 'Agendamento atualizado' : 'Agendamento criado')
    } catch (e) {
      // O master devolve o motivo em data.message — mostrar isso é melhor que
      // "erro ao salvar", porque quase sempre é regra de negócio (teto de
      // destinatários, telefone inválido, recorrência sem data futura).
      notify(e?.response?.data?.message || 'Falha ao salvar o agendamento')
      console.error(e)
    } finally {
      setIsSaving(false)
    }
  }

  const alternarStatus = async (item) => {
    try {
      await sm.setStatus(api, secret, item._id, item.status === 'active' ? 'paused' : 'active')
      await carregar()
      notify(item.status === 'active' ? 'Agendamento pausado' : 'Agendamento retomado')
    } catch (e) {
      notify(e?.response?.data?.message || 'Falha ao alterar o status')
    }
  }

  const confirmarExclusao = async () => {
    if (!paraExcluir) return
    try {
      await sm.remove(api, secret, paraExcluir._id)
      setParaExcluir(null)
      await carregar()
      notify('Agendamento excluído')
    } catch (e) {
      notify(e?.response?.data?.message || 'Falha ao excluir o agendamento')
    }
  }

  const temAcesso = user?.plan?.functionalities?.includes(FUNCIONALIDADE)

  // Sem `user` o contexto ainda está carregando — spinner, não tela vazia.
  if (!user) {
    return <div className="flex justify-center p-12"><CircularProgress /></div>
  }

  // O item some do menu sem a funcionalidade, mas a URL continua digitável:
  // sem esta guarda, dava pra entrar direto pelo endereço.
  if (!temAcesso) {
    window.location.href = '/'
    return null
  }

  return (
    <div className="p-4">
      <div className="flex items-center space-x-2 mb-1">
        <ScheduleSendIcon style={{ color: theme.palette.primary.main }} />
        <Text className="text-lg md:text-xl font-medium">Mensagens agendadas</Text>
      </div>
      <Text className="font-light text-sm mb-4">
        Envios que saem sozinhos, no dia e na hora que você definir.
      </Text>

      <Paper variant="outlined" sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        <div className="p-5">
          <div className="flex justify-end mb-4">
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<AddIcon />}
              disabled={isSaving}
              onClick={() => setEmEdicao({})}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '9px' }}
            >
              Novo agendamento
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><CircularProgress size={28} /></div>
          ) : !itens.length ? (
            <div className="text-center py-10">
              <ScheduleSendIcon style={{ fontSize: 42, opacity: 0.35 }} />
              <Text className="font-medium mt-2">Nenhum agendamento ainda</Text>
              <Text className="font-light text-sm mt-1">
                Crie um aviso de vencimento que sai todo dia às 9h, uma cobrança toda segunda,
                ou uma pesquisa no primeiro dia do mês. Você monta uma vez e ele trabalha sozinho.
              </Text>
            </div>
          ) : (
            <div className="flex flex-col space-y-3">
              {itens.map((item) => {
                const status = STATUS[item.status] || STATUS.finished
                const proxima = formatarProxima(item.nextRunAt)
                const total = item?.audience?.numbers?.length || 0

                return (
                  <div
                    key={item._id}
                    className="flex flex-wrap items-start gap-3 p-4 rounded-xl"
                    style={{ border: `1px solid ${theme.palette.divider}` }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2">
                        <Text className="font-medium">{item.title}</Text>
                        <Selo texto={status.rotulo} cor={status.cor} />
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                        <Text className="font-light text-sm">
                          {descreverRecorrencia(item.schedule)}
                        </Text>
                        <Text className="font-light text-sm">
                          {total} destinatário{total === 1 ? '' : 's'}
                        </Text>
                        {proxima && item.status === 'active' && (
                          <Text
                            className="text-sm font-medium"
                            style={{ color: theme.palette.primary.main }}
                          >
                            próxima {proxima}
                          </Text>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center">
                      {item.status !== 'finished' && (
                        <Tooltip title={item.status === 'active' ? 'Pausar' : 'Retomar'}>
                          <IconButton size="small" onClick={() => alternarStatus(item)}>
                            {item.status === 'active' ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Histórico de execuções">
                        <IconButton size="small" onClick={() => setVerHistoricoDe(item)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => setEmEdicao(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setParaExcluir(item)
                            setIsOpenConfirmationModalExcluir(true)
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </Paper>

      {emEdicao && (
        <Editor
          aberto={!!emEdicao}
          agendamento={emEdicao}
          salvando={isSaving}
          onFechar={() => setEmEdicao(null)}
          onSalvar={salvar}
        />
      )}

      {verHistoricoDe && (
        <Historico
          aberto={!!verHistoricoDe}
          agendamento={verHistoricoDe}
          api={api}
          secret={secret}
          onFechar={() => setVerHistoricoDe(null)}
          notify={notify}
        />
      )}

      <ConfirmationModal
        open={isOpenConfirmationModalExcluir}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <DeleteIcon style={{ fontSize: 32, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">Excluir agendamento</Text>
          </div>
        }
        onClose={() => setIsOpenConfirmationModalExcluir(false)}
        onConfirm={confirmarExclusao}
      >
        <Text className="font-bold">Essa ação é irreversível.</Text>
        <Text>
          Excluir "{paraExcluir?.title}"? As execuções que ainda não saíram são canceladas junto.
        </Text>
      </ConfirmationModal>

      <Snackbar
        open={!!mensagem}
        autoHideDuration={5000}
        onClose={() => setMensagem('')}
        message={mensagem}
      />
    </div>
  )
}
